import { api } from '@/helpers/api'
import { MenuKeyEnum, UserPermissionEnum } from '@/helpers/commonEnum'
import { storageHelper } from '@/helpers/storageHelper'
import { makeObservable, observable, action, computed, autorun } from 'mobx'
import { routerService } from '../routerService'
import { commonUtils } from '@/helpers/commonUtils'

class UserService {
  @observable token: string = ''
  /** 用户信息 */
  @observable userData: ServerData.UserInfo | null = null
  /** 工作区 */
  @observable workspaceData: ServerData.UserInfo['workspaceList'][number] | null = null
  /** 菜单key */
  @observable menuKey: StorageData['menuKey'] = { type: 'Menu', key: '' }
  /** 素材来源 */
  @observable fileSource: ServerData.FileSourceData[] = []

  constructor() {
    makeObservable(this)
  }

  @computed get isLogin() {
    return !!this.token
  }

  @computed get isVisitor() {
    return this.workspaceData?.userPermission === UserPermissionEnum.Anonymous
  }

  @computed get isAdmin() {
    return [UserPermissionEnum.Admin, UserPermissionEnum.SuperAdmin].includes(
      this.workspaceData?.userPermission ?? UserPermissionEnum.User
    )
  }

  @computed get workspaceId() {
    return this.workspaceData?.workspaceId ?? ''
  }

  @computed get workspaceUserPermission() {
    return this.workspaceData?.userPermission ?? UserPermissionEnum.User
  }

  @computed get workspaceOpenAnonymous() {
    return this.workspaceData?.openAnonymous ?? false
  }

  @computed get fileSourceOptions() {
    return this.fileSource.map((item) => {
      return {
        label: item.sourceName,
        value: item.sourceName,
      }
    })
  }

  public init() {
    const { workspaceId } = storageHelper.get(['workspaceId'])
    const { menuKey } = storageHelper.get(['menuKey'])
    const token = commonUtils.getCookie('token')
    this.token = token || ''

    autorun(() => {
      if (this.workspaceId) {
        this.getFileSource()
      }
    })

    if (this.token) {
      this.menuKey = menuKey || { type: 'Menu', key: '' }
      this.getUserInfo(workspaceId)
    }

    // this.getEnumData()
  }

  // 登录
  @action
  async login(values: PostData.LoginModel) {
    const res = await api.user.login(values)
    if (res.success) {
      this.token = res.data.token
      commonUtils.setCookie('token', this.token)
      await this.getUserInfo()
      return true
    }
  }

  // 登出
  @action
  public logout() {
    this.token = ''
    this.userData = null
    this.workspaceData = null
    commonUtils.deleteCookie('token')
    storageHelper.set({
      menuKey: { type: 'Menu', key: '' },
      workspaceId: '',
    })
    routerService.navigateTo('login')
  }

  // 获取用户信息
  @action
  public async getUserInfo(workspaceId?: string | number) {
    const res = await api.user.getUserInfo()
    if (res.success) {
      this.userData = res.data
      const workspace = res.data.workspaceList.find(
        (item) => `${item.workspaceId}` === `${workspaceId}`
      )
      this.workspaceData = workspace || res.data.workspaceList[0]
      if (!this.menuKey.key) {
        this.menuKey = {
          type: 'Menu',
          key: this.isAdmin ? MenuKeyEnum.Review : MenuKeyEnum.MaterialLibrary,
        }
      } else if (!this.isAdmin) {
        this.menuKey = {
          type: 'Menu',
          key: MenuKeyEnum.MaterialLibrary,
        }
      }
      storageHelper.set({
        workspaceId: this.workspaceData?.workspaceId ?? '',
        menuKey: this.menuKey,
      })
    }
  }

  // 设置菜单key
  @action
  public setMenuKey(key: StorageData['menuKey']) {
    this.menuKey = key
    storageHelper.set({
      menuKey: key,
    })
  }

  // 获取素材来源
  @action
  public async getFileSource() {
    const res = await api.material.getSource(this.workspaceId)
    if (res.success) {
      this.fileSource = res.data
    }
  }

  // 设置工作区
  @action
  public setWorkspaceId(workspaceId: string) {
    if (workspaceId) {
      storageHelper.set({
        workspaceId: workspaceId,
        menuKey: { type: 'Menu', key: '' },
      })
      window.location.reload()
    }
  }

  // // 获取枚举数据
  // @action
  // public async getEnumData() {
  //   const res = await api.user.getEnumData()
  //   if (res.success) {
  //     this.copyrightEnum = res.data.copyrightEnum
  //   }
  // }
}

export const userService = new UserService()
